import { Metric, SectionLabel, priceOf, PricingPanel } from "@/components/Metric";
import NestingCanvas from "@/components/NestingCanvas";
import { SaveQuoteBar } from "@/components/SaveQuote";
import { money } from "@/lib/format";
import { Hash, Percent, Tag, Shirt, Ruler, Layers } from "lucide-react";

const sizesText = (s) => Object.entries(s || {}).map(([k, v]) => (k === "ALL" ? `${v}` : `${k}:${v}`)).join("  ");

function LinesTable({ res }) {
  const isRetail = res.retail_total != null;
  const showCost = res.lines.some((l) => l.garment_cost != null);
  const showGarment = res.lines.some((l) => !l.customer_supplied);
  return (
    <table className="w-full text-sm" data-testid="apparel-lines-table">
      <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-slate-100 text-left">
        <th className="py-1.5">Item</th><th>Sizes</th>
        {showGarment && showCost && <th className="text-right" title="What you pay per blank garment">Garment cost</th>}
        {showGarment && isRetail && <th className="text-right" title="Blank garment sale price">Garment sale</th>}
        <th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Total</th>
      </tr></thead>
      <tbody>
        {res.lines.map((l, i) => (
          <tr key={i} className="border-b border-slate-50 align-top" data-testid={`apparel-line-${i}`}>
            <td className="py-1.5"><div className="font-semibold">{l.name}</div><div className="text-[11px] text-slate-400">{l.customer_supplied ? "Customer-supplied" : l.color}{l.big_qty ? ` · ${l.big_qty} big size` : ""}</div></td>
            <td className="text-[11px] font-mono text-slate-500">{sizesText(l.sizes)}</td>
            {showGarment && showCost && <td className="text-right num text-slate-500" data-testid={`apparel-line-${i}-gcost`}>{l.customer_supplied ? "—" : money(l.garment_cost)}</td>}
            {showGarment && isRetail && <td className="text-right num" data-testid={`apparel-line-${i}-gsale`}>{l.customer_supplied ? "—" : money(l.garment_retail_each)}{showCost && !l.customer_supplied && <div className="text-[10px] text-emerald-600">+{money(l.garment_retail_each - l.garment_cost)}</div>}</td>}
            <td className="text-right num">{l.qty}</td>
            <td className="text-right num">{money(isRetail ? l.unit_retail : l.unit_wholesale)}</td>
            <td className="text-right num font-semibold">{money(isRetail ? l.line_retail : l.line_wholesale)}</td>
          </tr>
        ))}
        {res.fees.map((f, i) => (
          <tr key={`f${i}`} className="border-b border-slate-50 text-slate-600" data-testid={`apparel-fee-${i}`}>
            <td colSpan={99} className="py-1.5 text-xs">{f.label}<span className="float-right num">{money(isRetail ? f.retail : f.wholesale)}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ApparelResults({ res, inputs, onRush }) {
  return (
    <div className="space-y-6" data-testid="apparel-results">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric icon={Hash} label="Pieces" value={res.quantity} />
        <Metric icon={Percent} label="Volume disc." value={`${res.volume_discount_pct || 0}%`} />
        <Metric icon={Shirt} label="Avg / piece" value={money(res.unit_price ?? res.wholesale_unit)} />
        {priceOf(res) != null && <Metric icon={Tag} label={res.retail_total != null ? "Retail" : "Wholesale"} value={money(priceOf(res))} accent />}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="apparel-consumption">
        <Metric icon={Ruler} label="DTF film (order)" value={`${res.order_film_length_in || 0}"`} sub={`${res.order_film_sqft || 0} ft² · ${res.inhouse_pieces || 0} pcs`} />
        {res.order_dtf_film_cost != null && <Metric icon={Tag} label="Film cost" value={money(res.order_dtf_film_cost)} />}
        <Metric icon={Layers} label="Sheets (supplied)" value={res.total_sheets || 0} sub={res.supplied_pieces ? `${res.sheets_per_supplied_piece}/pc · ${money(res.sheet_unit_price)}` : "no customer garments"} />
        <Metric icon={Hash} label="Stitches (order)" value={(res.order_stitches || 0).toLocaleString()} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <SectionLabel>Order breakdown</SectionLabel>
        <div className="overflow-x-auto"><LinesTable res={res} /></div>
        <p className="text-[11px] text-slate-400 mt-2">
          {res.placements.map((p) => `${p.label} (${p.method === "dtf" ? `DTF ${p.w}×${p.h}"` : `${p.stitch_count.toLocaleString()} st`})`).join(" · ")}
          {res.sheets_per_supplied_piece > 0 && ` · Customer garments: ${res.sheets_per_supplied_piece} sheet/pc at ${money(res.sheet_unit_price)}`}
        </p>
        <PricingPanel r={res} className="mt-3" onRush={onRush} />
        {res.layout && <div className="mt-4"><SectionLabel>DTF film layout — whole order ({res.order_film_length_in}" × {res.layout.bin_width}")</SectionLabel><NestingCanvas layout={res.layout} />{res.layout_truncated && <p className="text-[11px] text-slate-400 mt-1">Preview shows the first 1,500 transfers; length and cost include all.</p>}</div>}
        <div className="mt-3 flex justify-end"><SaveQuoteBar module="Apparel" title={`Apparel x${res.quantity}`} inputs={inputs} summary={res} /></div>
      </div>
    </div>
  );
}
